import React, { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingForm from "@/components/BookingForm";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import heroCar from "@/assets/hero-car.jpg";
import { useTranslation } from "react-i18next";

interface LandingPageLayoutProps {
  translationKey: string;
}

const LandingPageLayout = ({ translationKey }: LandingPageLayoutProps) => {
  const { t } = useTranslation();
  
  const title = t(`landingPages.${translationKey}.title`);
  const subtitle = t(`landingPages.${translationKey}.subtitle`);
  const desc = t(`landingPages.${translationKey}.desc`);
  const seoTitle = t(`landingPages.${translationKey}.seoTitle`);
  const seoDesc = t(`landingPages.${translationKey}.seoDesc`);

  const contentTitle = t(`landingPages.${translationKey}.contentTitle`);
  const contentText = t(`landingPages.${translationKey}.contentText`);
  const feature1Title = t(`landingPages.${translationKey}.feature1Title`);
  const feature1Desc = t(`landingPages.${translationKey}.feature1Desc`);
  const feature2Title = t(`landingPages.${translationKey}.feature2Title`);
  const feature2Desc = t(`landingPages.${translationKey}.feature2Desc`);
  const feature3Title = t(`landingPages.${translationKey}.feature3Title`);
  const feature3Desc = t(`landingPages.${translationKey}.feature3Desc`);
  useEffect(() => {
    if (seoTitle) {
      document.title = seoTitle;
    }
    if (seoDesc) {
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", seoDesc);
      } else {
        const meta = document.createElement('meta');
        meta.name = "description";
        meta.content = seoDesc;
        document.head.appendChild(meta);
      }
    }
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [seoTitle, seoDesc]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar />
      
      {/* Hero Section matching the main website design */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroCar}
            alt={title}
            width="1920"
            height="1080"
            className="w-full h-full object-cover"
            fetchpriority="high"
            loading="eager"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="absolute inset-0 overflow-hidden pointer-events-none w-full h-full">
          {[...Array(40)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-primary/40"
              initial={{ left: `${Math.random() * 100}%`, top: -20, opacity: 0 }}
              animate={{ top: "110%", opacity: [0, 0.8, 0] }}
              transition={{ duration: 5 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 5, ease: "linear" }}
              style={{ filter: "blur(0.5px)" }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-shadow-gold text-foreground"
          >
            {title}
            <br />
            <span className="gold-text">{subtitle}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-foreground/60 mb-10 max-w-2xl mx-auto"
          >
            {desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <a href="#booking" className="gold-gradient px-10 py-4 rounded-full text-primary-foreground font-bold text-lg hover:opacity-90 transition-all animate-pulse-gold">
              {t("hero.bookNow")}
            </a>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="text-primary/60" size={32} />
        </motion.div>
      </section>

      {/* Unique SEO Content Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                {contentTitle}
              </h2>
              <div className="w-20 h-1 gold-gradient mb-8 rounded-full" />
              <p className="text-lg text-muted-foreground leading-relaxed italic border-l-4 border-primary/40 pl-6 py-2">
                {contentText}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { title: feature1Title, desc: feature1Desc },
                { title: feature2Title, desc: feature2Desc },
                { title: feature3Title, desc: feature3Desc }
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card p-8 rounded-3xl border border-primary/10 hover:border-primary/30 transition-all"
                >
                  <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="py-16">
        <BookingForm />
      </div>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default LandingPageLayout;
